import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, User, LogOut, Package, Book, Users, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useProfile } from '../../hooks/useProfile';
import { useGroceryLists } from '../../hooks/useGroceryLists';
import { useGuestList } from '../../hooks/useGuestList';
import NotificationBell from '../Community/NotificationBell';

const Navbar = () => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const { activeList } = useGroceryLists();
  const { guestList } = useGuestList();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  // Filter navLinks based on authentication
  const allNavLinks = [
    { path: '/products', label: 'Products', icon: <Package size={18} />, public: true },
    { path: '/recipes', label: 'Recipes', icon: <Book size={18} />, public: true },
    { path: '/community', label: 'Community', icon: <Users size={18} />, public: true },
    { path: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} />, public: false },
  ];

  const navLinks = user 
    ? allNavLinks 
    : allNavLinks.filter(link => link.public);

  const handleLogout = async () => {
    try {
      await signOut();
      setProfileMenuOpen(false);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Authenticated user list
  const activeListItemCount = activeList?.items?.length || 0;
  const uncheckedCount = activeList?.items?.filter(item => !item.isChecked).length || 0;

  // Guest list
  const guestListItemCount = guestList?.items?.length || 0;
  const guestUncheckedCount = guestList?.items?.filter(item => !item.isChecked).length || 0;

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-text-tertiary/10">
      <div className="max-w-7xl mx-auto px-lg">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-sm">
            <span className="text-title-1 font-rounded text-primary">Nutrius</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-md">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center gap-sm px-md py-sm rounded-medium transition-all ${
                  isActive(link.path)
                    ? 'bg-primary text-white'
                    : 'text-text-secondary hover:text-primary hover:bg-primary/5'
                }`}
              >
                {link.icon}
                <span className="text-callout font-semibold">{link.label}</span>
              </Link>
            ))}
          </div>

          {/* Right Side: Cart & Profile */}
          <div className="flex items-center gap-md">
            {/* Shopping Cart - Authenticated Users */}
            {user && (
              <Link
                to="/products?tab=shopping-list"
                className="relative p-sm rounded-full hover:bg-primary/5 transition-all group"
              >
                <ShoppingCart size={24} className="text-primary" />
                
                {activeListItemCount > 0 && (
                  <div className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-primary flex items-center justify-center px-1">
                    <span className="text-footnote font-bold text-white">
                      {uncheckedCount > 99 ? '99+' : uncheckedCount}
                    </span>
                  </div>
                )}

                {activeList && (
                  <div className="absolute right-0 top-full mt-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <div className="bg-background border border-text-tertiary/20 rounded-medium shadow-lg p-sm whitespace-nowrap">
                      <p className="text-caption font-semibold text-primary">
                        {activeList.name}
                      </p>
                      <p className="text-footnote text-text-secondary">
                        {uncheckedCount} items
                      </p>
                    </div>
                  </div>
                )}
              </Link>
            )}

            {/* Shopping Cart - Guest Users */}
            {!user && (
              <Link
                to="/products?tab=shopping-list"
                className="relative p-sm rounded-full hover:bg-primary/5 transition-all group"
              >
                <ShoppingCart size={24} className="text-primary" />
                
                {guestListItemCount > 0 && (
                  <div className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-accent flex items-center justify-center px-1">
                    <span className="text-footnote font-bold text-white">
                      {guestUncheckedCount > 99 ? '99+' : guestUncheckedCount}
                    </span>
                  </div>
                )}

                <div className="absolute right-0 top-full mt-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <div className="bg-background border border-text-tertiary/20 rounded-medium shadow-lg p-sm whitespace-nowrap">
                    <p className="text-caption font-semibold text-accent">
                      Guest List
                    </p>
                    <p className="text-footnote text-text-secondary">
                      {guestUncheckedCount} items
                    </p>
                  </div>
                </div>
              </Link>
            )}

            {/* Notification Bell - Authenticated Users */}
            {user && <NotificationBell />}

            {/* User Profile or Auth Buttons */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center gap-sm px-md py-sm rounded-full hover:bg-primary/5 transition-all"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User size={18} className="text-primary" />
                  </div>
                  <span className="text-callout font-semibold text-primary hidden lg:block">
                    {profile?.displayName || user.displayName || user.email?.split('@')[0]}
                  </span>
                </button>

                {profileMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setProfileMenuOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-sm w-48 bg-background border border-text-tertiary/20 rounded-large shadow-lg overflow-hidden z-50">
                      <div className="p-md border-b border-text-tertiary/10">
                        <p className="text-callout font-semibold text-primary truncate">
                          {profile?.displayName || user.displayName}
                        </p>
                        {profile?.username && (
                          <p className="text-caption text-text-tertiary truncate">
                            @{profile.username}
                          </p>
                        )}
                        <p className="text-caption text-text-secondary truncate">
                          {user.email}
                        </p>
                      </div>
                      
                      <Link
                        to="/dashboard"
                        className="block px-md py-sm hover:bg-primary/5 text-callout text-primary transition-colors"
                        onClick={() => setProfileMenuOpen(false)}
                      >
                        Dashboard
                      </Link>
                      
                      <Link
                        to="/profile"
                        className="block px-md py-sm hover:bg-primary/5 text-callout text-primary transition-colors"
                        onClick={() => setProfileMenuOpen(false)}
                      >
                        Profile Settings
                      </Link>
                      
                      <button
                        onClick={handleLogout}
                        className="w-full px-md py-sm text-left hover:bg-error/5 text-callout text-error transition-colors flex items-center gap-sm"
                      >
                        <LogOut size={16} />
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-sm">
                <Link to="/login">
                  <button className="px-lg py-sm text-callout font-semibold text-primary hover:text-primary/80 transition-colors">
                    Sign In
                  </button>
                </Link>
                <Link to="/signup">
                  <button className="px-lg py-sm bg-primary text-white rounded-full text-callout font-semibold hover:bg-primary/90 transition-colors">
                    Sign Up
                  </button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-sm rounded-medium hover:bg-primary/5 transition-all"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X size={24} className="text-primary" />
              ) : (
                <Menu size={24} className="text-primary" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-lg border-t border-text-tertiary/10">
            <div className="flex flex-col gap-sm">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-sm px-md py-md rounded-medium transition-all ${
                    isActive(link.path)
                      ? 'bg-primary text-white'
                      : 'text-text-secondary hover:text-primary hover:bg-primary/5'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.icon}
                  <span className="text-callout font-semibold">{link.label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
